import type { DocPageContent } from '../types';

/**
 * "Connections" — Dashboard section (ADR-0074).
 *
 * The hub that joins Syncanix to everything else, and the last sidebar surface
 * the docs audit found with no page. Three of its five areas were undocumented
 * entirely — webhook destinations, the widget-origin allow-list, and the custom
 * domain — and the origin allow-list in particular is the one a stuck
 * integrator needs: a widget that will not load on a staging host is almost
 * always an origin nobody added.
 *
 * Reconciled from apps/web `features/connections` (route `/connections`) and
 * the `connections.*` / `widgetorigins.*` / `webhooks.*` catalogs. The MCP and
 * provider-key areas have their own deeper pages; this one says what each is
 * for and links onward rather than repeating them.
 */
export const DASHBOARD_CONNECTIONS_PAGE: DocPageContent = {
  slug: 'dashboard/connections',
  titleKey: 'docs.dashboard.connections.title',
  descriptionKey: 'docs.dashboard.connections.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.dashboard.connections.lead' },

    {
      kind: 'heading',
      level: 2,
      id: 'origins',
      textKey: 'docs.dashboard.connections.origins.heading',
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.connections.origins.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.connections.origins.item.what',
        'docs.dashboard.connections.origins.item.staging',
        'docs.dashboard.connections.origins.item.remove',
      ],
    },
    {
      kind: 'callout',
      tone: 'tip',
      titleKey: 'docs.dashboard.connections.origins.stuck.title',
      textKey: 'docs.dashboard.connections.origins.stuck.body',
    },

    {
      kind: 'heading',
      level: 2,
      id: 'webhooks',
      textKey: 'docs.dashboard.connections.webhooks.heading',
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.connections.webhooks.body' },
    {
      kind: 'steps',
      steps: [
        {
          titleKey: 'docs.dashboard.connections.webhooks.step.url.title',
          textKey: 'docs.dashboard.connections.webhooks.step.url.text',
        },
        {
          titleKey: 'docs.dashboard.connections.webhooks.step.events.title',
          textKey: 'docs.dashboard.connections.webhooks.step.events.text',
        },
        {
          titleKey: 'docs.dashboard.connections.webhooks.step.secret.title',
          textKey: 'docs.dashboard.connections.webhooks.step.secret.text',
        },
      ],
    },

    {
      kind: 'heading',
      level: 2,
      id: 'domain',
      textKey: 'docs.dashboard.connections.domain.heading',
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.connections.domain.body' },

    { kind: 'heading', level: 2, id: 'mcp', textKey: 'docs.dashboard.connections.mcp.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.connections.mcp.body' },

    {
      kind: 'heading',
      level: 2,
      id: 'provider',
      textKey: 'docs.dashboard.connections.provider.heading',
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.connections.provider.body' },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.dashboard.connections.next.mcp.title',
          textKey: 'docs.dashboard.connections.next.mcp.text',
          to: { docSlug: 'mcp/connect' },
        },
        {
          titleKey: 'docs.dashboard.connections.next.byok.title',
          textKey: 'docs.dashboard.connections.next.byok.text',
          to: { docSlug: 'billing/byok' },
        },
        {
          titleKey: 'docs.dashboard.connections.next.embed.title',
          textKey: 'docs.dashboard.connections.next.embed.text',
          to: { docSlug: 'widget/embedding' },
        },
      ],
    },
  ],
};
